import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, CheckCircle2, Car, Calendar, CreditCard,
  Sparkles, Droplets, AlertCircle, FileText, History,
  RefreshCw, XCircle, Loader2, ArrowRightLeft, AlertTriangle,
  RotateCcw, HelpCircle
} from 'lucide-react';

const PLAN_STATUS = {
  active: { label: 'Ativo', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function PlanDetails() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [hasActivePlan, setHasActivePlan] = useState(false);
  const [cancellingPlan, setCancellingPlan] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    fetchSubscriptionDetails();
  }, [user, navigate]);

  const fetchSubscriptionDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/client/subscription-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHasActivePlan(res.data.has_active_plan);
      setSubscription(res.data.subscription);
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const handleCancelPlan = async () => {
    if (!confirm('Seu plano será cancelado ao final do período atual. Você poderá usar os benefícios até essa data. Deseja continuar?')) {
      return;
    }

    setCancellingPlan(true);
    try {
      const token = localStorage.getItem('token');
      const isBilling = subscription?.billing_mode === 'billing';
      const url = isBilling
        ? `${API}/client/subscriptions/${subscription.id}/cancel-renewal`
        : `${API}/client/cancel-subscription`;
      const res = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchSubscriptionDetails(); // Recarregar dados
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar plano');
    } finally {
      setCancellingPlan(false);
    }
  };

  const handleReactivatePlan = async () => {
    setReactivating(true);
    try {
      const token = localStorage.getItem('token');
      const isBilling = subscription?.billing_mode === 'billing';
      const url = isBilling
        ? `${API}/client/subscriptions/${subscription.id}/reactivate-renewal`
        : `${API}/client/reactivate-subscription`;
      const res = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reativar plano');
    } finally {
      setReactivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Se não tem plano ativo, redirecionar para escolha de planos
  if (!hasActivePlan) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-lg mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/client/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <Card className="p-8 text-center">
            <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-xl font-bold mb-2">Sem plano ativo</h2>
            <p className="text-muted-foreground mb-6">
              Você ainda não possui um plano ativo. Assine um plano para aproveitar todos os benefícios!
            </p>
            <Button onClick={() => navigate('/client/plans')} data-testid="choose-plan-btn">
              Ver planos disponíveis
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const statusConfig = subscription?.cancel_at_period_end 
    ? { label: 'Cancelamento agendado', color: 'bg-amber-100 text-amber-800', icon: AlertTriangle }
    : (PLAN_STATUS[subscription?.status] || PLAN_STATUS.active);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="plan-details-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/client/dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Detalhes do Plano</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Aviso de cancelamento agendado */}
        {subscription?.cancel_at_period_end && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Cancelamento agendado.</strong> Seu plano será cancelado em {formatDate(subscription?.cancellation_effective_date)}.
              Você pode usar os benefícios até essa data.
              <Button 
                variant="link" 
                className="p-0 h-auto ml-2 text-amber-800 underline"
                onClick={handleReactivatePlan}
                disabled={reactivating}
              >
                {reactivating ? 'Reativando...' : 'Manter plano'}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Aviso de downgrade pendente */}
        {subscription?.pending_plan_change && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Alteração pendente.</strong> Seu plano será alterado para "{subscription.pending_plan_change.name}" 
              ({formatCurrency(subscription.pending_plan_change.price)}/mês) na próxima renovação.
            </AlertDescription>
          </Alert>
        )}

        {/* Card Principal - Informações do Plano */}
        <Card className="overflow-hidden" data-testid="plan-info-card">
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/70 text-xs uppercase tracking-wide mb-1">Meu Plano</p>
                <h2 className="text-2xl font-bold" data-testid="plan-name">
                  {subscription?.plan?.name}
                </h2>
              </div>
              <Badge className={`${statusConfig.color} flex items-center gap-1`}>
                <StatusIcon className="w-3 h-3" />
                {statusConfig.label}
              </Badge>
            </div>
            
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-3xl font-bold" data-testid="plan-price">
                {formatCurrency(subscription?.plan?.price)}
              </span>
              <span className="text-white/70">/mês</span>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Datas */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Data de início</p>
                <p className="font-medium flex items-center gap-1" data-testid="start-date">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  {formatDate(subscription?.start_date)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {subscription?.cancel_at_period_end ? 'Válido até' : 'Próxima renovação'}
                </p>
                <p className="font-medium flex items-center gap-1" data-testid="renewal-date">
                  <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  {formatDate(subscription?.next_billing_date)}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Card de Serviços Incluídos */}
        <Card className="p-5" data-testid="services-card">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Serviços Incluídos
          </h3>

          <div className="space-y-4">
            {/* Lavagem Completa */}
            {subscription?.usage?.complete?.total > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Lavagem Completa</p>
                    <p className="text-xs text-muted-foreground">Limpeza interna e externa</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-600" data-testid="usage-complete">
                    {subscription?.usage?.complete?.used}/{subscription?.usage?.complete?.total}
                  </p>
                  <p className="text-xs text-muted-foreground">usadas</p>
                </div>
              </div>
            )}

            {/* Ducha Simples */}
            {subscription?.usage?.ducha?.total > 0 && (
              <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-cyan-100 p-2 rounded-lg">
                    <Droplets className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium">Ducha Simples</p>
                    <p className="text-xs text-muted-foreground">Lavagem externa rápida</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-cyan-600" data-testid="usage-ducha">
                    {subscription?.usage?.ducha?.used}/{subscription?.usage?.ducha?.total}
                  </p>
                  <p className="text-xs text-muted-foreground">usadas</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Card do Veículo Vinculado */}
        <Card className="p-5" data-testid="vehicle-card">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Car className="w-5 h-5 text-primary" />
            Veículo Vinculado
          </h3>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg border">
                <Car className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="font-medium" data-testid="vehicle-info">
                  {subscription?.vehicle?.brand} {subscription?.vehicle?.model}
                </p>
                <p className="text-sm text-muted-foreground">
                  Placa: {subscription?.vehicle?.plate}
                </p>
              </div>
            </div>
            <Badge variant="outline" data-testid="vehicle-type">
              {subscription?.vehicle?.vehicle_type_display}
            </Badge>
          </div>
          
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Para trocar de veículo, use a opção "Migrar Plano"
          </p>
        </Card>

        {/* Card de Regras */}
        <Card className="p-5" data-testid="rules-card">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Informações do Plano
          </h3>

          <div className="space-y-2">
            {subscription?.rules?.map((rule, index) => (
              <div key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{rule}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Ações */}
        <div className="space-y-3">
          {/* Botão Migrar Plano - PRIMÁRIO (destaque principal) */}
          {!subscription?.cancel_at_period_end && (
            <Button 
              className="w-full"
              onClick={() => navigate('/client/migrate-plan')}
              data-testid="migrate-plan-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Migrar Plano
            </Button>
          )}

          {/* Botão Ajuda */}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/client/help')}
            data-testid="help-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Ajuda
          </Button>

          {/* Link Voltar ao Dashboard - NAVEGAÇÃO DISCRETA */}
          <button 
            type="button"
            onClick={() => navigate('/client/dashboard')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Dashboard
          </button>

          {/* Espaçamento maior antes do botão perigoso */}
          <div className="pt-4">
            <Separator />
          </div>

          {/* Botão Cancelar ou Reativar Plano - ISOLADO */}
          <div className="pt-2">
            {subscription?.cancel_at_period_end ? (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleReactivatePlan}
                disabled={reactivating}
                data-testid="reactivate-plan-btn"
              >
                {reactivating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reativando...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Manter plano ativo
                  </>
                )}
              </Button>
            ) : (
              <Button 
                variant="ghost" 
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleCancelPlan}
                disabled={cancellingPlan}
                data-testid="cancel-plan-btn"
              >
                {cancellingPlan ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar plano
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
